import { uuid4 } from '@sentry/core'
import { Link } from '@tanstack/react-router'
import { Collapse, CollapseProps } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import {
  Activity,
  deconstructAuditLog,
} from 'features/assetDatabase/utils/activityLogHelpers'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import {
  GeckoAssetModel,
  GeckoInspection,
  GeckoMachinedForgingSimple,
  GeckoMillPlan,
  getSortedAssetModels,
  machinedForgingStageColorMap,
  MachinedForgingStageContainer,
  RolodexAuditLogJson,
  RolodexAuditLogObject,
} from 'types'
import { parseDate } from 'utils/parsingHelpers'

const PageContainer = styled.div`
  margin-top: 0.6rem;
  width: 100%;
  overflow: auto;
`

const EventContainer = styled.div`
  text-align: left;
`

const TimestampText = styled.div`
  font-size: 0.85em;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-bottom: 0.1rem;
`

const EventText = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 0.1rem;
`

const SmallLinkText = styled(Link)`
  font-size: 0.9em;
  color: ${({ theme }) => theme.forecast.defaultText};
  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
  text-decoration: underline;
  display: block;
`

interface EventType {
  id: string
  type: 'inspection' | 'mill-plan' | 'status'
  event_text: string
  date: Date
  person: string
  url?: string
}

// Use GeckoAssetModel if it includes the necessary fields (id, data)
// Otherwise, define a specific interface here

interface EventsListProps {
  // Use the appropriate type for assetModels, e.g., GeckoAssetModel[] or DisplayAssetModel[]
  assetModels: readonly GeckoAssetModel[]
  inspections: readonly GeckoInspection[]
  millPlans: readonly GeckoMillPlan[]
  assetAuditLogs: RolodexAuditLogObject | undefined
  sortAscending: boolean
}

const formatDate = (date: Date | undefined): string => {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }
  return 'N/A'
}

const getInspectionsByModelId = (
  inspections: readonly GeckoInspection[]
): {
  inspectionsByModelId: Record<string, GeckoInspection[]>
} => {
  const inspectionsByModelId: Record<string, GeckoInspection[]> = {}

  // Group inspections by their asset-model tag
  inspections.forEach((inspection) => {
    const modelId = inspection.tags['asset-model']

    if (modelId) {
      if (!inspectionsByModelId[modelId]) {
        inspectionsByModelId[modelId] = []
      }
      inspectionsByModelId[modelId].push(inspection)
    }
  })

  return { inspectionsByModelId }
}

const getMillPlansByModelId = (
  millPlans: readonly GeckoMillPlan[]
): {
  millPlansByModelId: Record<string, GeckoMillPlan[]>
} => {
  const millPlansByModelId: Record<string, GeckoMillPlan[]> = {}

  millPlans.forEach((millPlan) => {
    const modelId = millPlan.tags['asset-model']

    if (modelId) {
      if (!millPlansByModelId[modelId]) {
        millPlansByModelId[modelId] = []
      }
      millPlansByModelId[modelId].push(millPlan)
    }
  })

  return { millPlansByModelId }
}

const EventListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const StageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

export const EventsList: React.FC<EventsListProps> = ({
  assetModels,
  inspections,
  millPlans,
  assetAuditLogs,
  sortAscending = false,
}) => {
  // Memoize flattened audit logs
  const flattenedAuditLogs = useMemo(() => {
    if (!assetAuditLogs) return []
    return Object.values(assetAuditLogs).flat()
  }, [assetAuditLogs])

  // Memoize status events derived from audit logs
  const statusEventsByModelId = useMemo(() => {
    const events: Record<string, EventType[]> = {}
    flattenedAuditLogs.forEach((auditLogJson: RolodexAuditLogJson) => {
      if (!auditLogJson.new_value?.data?.status) return

      const date = parseDate(auditLogJson.timestamp)
      if (!date) return

      const modelId = auditLogJson.object_id
      if (!events[modelId]) {
        events[modelId] = []
      }

      const person = String(
        auditLogJson.metadata.user_name ||
          auditLogJson.metadata.user_email ||
          'Gecko Robotics'
      )

      const activityDetails: Activity[] = deconstructAuditLog(auditLogJson)

      const statusEvents = activityDetails
        .filter(
          (activity) =>
            activity.key === '$.data.status' &&
            auditLogJson.old_value?.data?.status
        )
        .map((): EventType => {
          const event_text = `${person} changed status from ${auditLogJson.old_value.data.status} to ${auditLogJson.new_value.data.status}`
          return {
            id: uuid4().slice(0, 8),
            type: 'status',
            event_text: event_text,
            date: date,
            person: person,
            url: undefined,
          }
        })

      events[modelId].push(...statusEvents)
    })
    return events
  }, [flattenedAuditLogs])

  const { inspectionsByModelId } = getInspectionsByModelId(inspections)
  const { millPlansByModelId } = getMillPlansByModelId(millPlans)

  const inspectionEventsByModelId = useMemo(() => {
    const events: Record<string, EventType[]> = {}
    Object.entries(inspectionsByModelId).forEach(
      ([modelId, modelInspections]) => {
        events[modelId] = modelInspections
          .map((inspection) => {
            const date = inspection.data.date
            return {
              id: inspection.id,
              event_text: 'Inspection Occurred',
              date,
              person: String(inspection.data.person || ''),
              url: `/details/${inspection.id}`,
              type: 'inspection',
            }
          })
          .filter(Boolean) as EventType[]
      }
    )
    return events
  }, [inspectionsByModelId])

  const millPlanEventsByModelId = useMemo(() => {
    const events: Record<string, EventType[]> = {}
    Object.entries(millPlansByModelId).forEach(([modelId, modelMillPlans]) => {
      events[modelId] = modelMillPlans
        .map((millPlan) => {
          const date = new Date(millPlan.updated_at)
          const componentId = millPlan.tags.component
          return {
            id: millPlan.id,
            type: 'mill-plan',
            event_text: 'Mill Plan Published',
            date,
            person: '',
            url: `/details/${componentId}?extension=MillPlanning`,
          }
        })
        .filter(Boolean) as EventType[]
    })
    return events
  }, [millPlansByModelId])

  const sortedAssetModels = getSortedAssetModels([...assetModels])

  if (!assetModels || assetModels.length === 0) {
    return <PageContainer>No asset models to display.</PageContainer>
  }

  const allEventsByModelId: Record<string, EventType[]> = {}
  sortedAssetModels.forEach((model) => {
    allEventsByModelId[model.id] = []
  })

  Object.keys(allEventsByModelId).forEach((modelId) => {
    const inspectionEvents = inspectionEventsByModelId[modelId] || []
    const modelStatusEvents = statusEventsByModelId[modelId] || []
    const millPlanEvents = millPlanEventsByModelId[modelId] || []
    const combinedEvents = [
      ...inspectionEvents,
      ...modelStatusEvents,
      ...millPlanEvents,
    ]
    allEventsByModelId[modelId] = combinedEvents.sort((a, b) => {
      const timeA = a.date instanceof Date ? a.date.getTime() : 0
      const timeB = b.date instanceof Date ? b.date.getTime() : 0
      const dateComparison = timeA - timeB
      return sortAscending ? dateComparison : -dateComparison
    })
  })

  const collapseItems = sortedAssetModels
    .map((model) => {
      const firstComponent = model.data.components?.size
        ? Array.from(model.data.components.values())[0]
        : undefined

      const modelEvents = allEventsByModelId[model.id] || []

      if (firstComponent instanceof GeckoMachinedForgingSimple) {
        const stage = firstComponent.stage
        const stageColor = stage
          ? machinedForgingStageColorMap.get(stage)
          : undefined

        const stageTag = stage ? (
          <MachinedForgingStageContainer
            bgColor={stageColor}
            textColor={CantileverTheme.colors.grayscale['700']}>
            {stage}
          </MachinedForgingStageContainer>
        ) : (
          'Unknown Stage'
        )

        return {
          key: model.id,
          label: (
            <StageContainer title={model.data.effective_date.toLocaleString()}>
              {`Advanced to stage `}
              {stageTag}
              {`on ${formatDate(model.data.effective_date)}`}
            </StageContainer>
          ),
          children: (
            <EventListWrapper>
              {modelEvents.map((event) => (
                <EventContainer key={event.id}>
                  <TimestampText title={event.date.toLocaleString()}>
                    {formatDistanceToNow(event.date, { addSuffix: true })}
                  </TimestampText>
                  <EventText>{event.event_text}</EventText>
                  {event.url && event.type === 'inspection' && (
                    <SmallLinkText
                      to={event.url}
                      from='/assets/$siteID/$unitID'>
                      View Inspection
                    </SmallLinkText>
                  )}
                  {event.url && event.type === 'mill-plan' && (
                    <SmallLinkText
                      to={event.url}
                      from='/assets/$siteID/$unitID'>
                      View Mill Plan
                    </SmallLinkText>
                  )}
                </EventContainer>
              ))}
              {modelEvents.length === 0 && <p>No events for this stage.</p>}
            </EventListWrapper>
          ),
        }
      }
      return null
    })
    .filter(Boolean) as CollapseProps['items']

  return (
    <PageContainer>
      <Collapse
        items={collapseItems}
        defaultActiveKey={
          sortedAssetModels[0]?.id ? [sortedAssetModels[0].id] : undefined
        }
      />
    </PageContainer>
  )
}
